/**
 * AgendaWidget: Shows events for the selected day with status pills and reminder info.
 * Read-only display; full CRUD to be added in v2.
 */

import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Clock, MapPin, Users, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CalendarEvent } from '@/lib/eventStore';

interface AgendaWidgetProps {
  events: CalendarEvent[];
  selectedDate: Date | null;
}

const STATUS_COLORS = {
  confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
  tentative: 'bg-gray-100 text-gray-700 border-gray-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200 line-through',
};

export const AgendaWidget: React.FC<AgendaWidgetProps> = ({ events, selectedDate }) => {
  if (!selectedDate || events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-muted-foreground">
        <AlertCircle className="w-8 h-8 mb-2 opacity-50" />
        <span className="text-sm">No events scheduled</span>
      </div>
    );
  }

  // Sort by start time
  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime());

  return (
    <div className="w-full h-full flex flex-col gap-3 overflow-y-auto p-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
        {format(selectedDate, 'EEEE, MMMM d, yyyy')}
      </div>

      {sorted.map((event) => (
        <div
          key={event.id}
          className={cn(
            'flex flex-col gap-1.5 p-3 rounded-lg border',
            STATUS_COLORS[event.status] || 'bg-background border-border'
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-semibold text-sm leading-tight flex-1">{event.title}</h4>
            <span className={cn('text-xs font-medium px-1.5 py-0.5 rounded-full border', STATUS_COLORS[event.status])}>
              {event.status}
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            {event.allDay ? (
              <span>All day</span>
            ) : (
              <span>
                {format(event.start, 'HH:mm')} – {format(event.end, 'HH:mm')}
              </span>
            )}
          </div>

          {event.location && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span>{event.location}</span>
            </div>
          )}

          {event.attendees && event.attendees.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Users className="w-3 h-3" />
              <span>{event.attendees.length} attendees</span>
            </div>
          )}

          {event.description && (
            <p className="text-xs text-muted-foreground line-clamp-2">{event.description}</p>
          )}

          {event.reminders && event.reminders.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
              <AlertCircle className="w-3 h-3" />
              <span>
                Reminder {event.reminders[0].minutesBefore > 0 ? `${event.reminders[0].minutesBefore}m before` : 'at time'}
              </span>
            </div>
          )}

          <div className="text-xs text-muted-foreground/60 mt-1">
            {event.source} • Updated {formatDistanceToNow(event.updatedAt, { addSuffix: true })}
          </div>
        </div>
      ))}
    </div>
  );
};
